import { CommonModule } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AutocompleteDropdownComponent } from '@app/shared/autocomplete-dropdown/autocomplete-dropdown.component';
import { ConfirmDialogComponent } from '@app/shared/confirm-dialog/confirm-dialog.component';
import { ContentBoxComponent } from '@app/shared/content-box/content-box.component';
import { CustomPaginatorIntl } from '@app/shared/custom-paginator/custom-paginator.component';
import { Labels } from '@core/constants/labels';
import { UserRole } from '@core/enums/user-role.enum';
import { ExamRequest } from '@core/models/exam-request.model';
import { Patient } from '@core/models/patient.model';
import { User } from '@core/models/user.model';
import { AuthService } from '@core/services/auth-service/auth-service.service';
import { DiscussionService } from '@core/services/discussion-service/discussion-service.service';
import { ExamService } from '@core/services/exam-service/exam-service.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ExamCardComponent } from '../exam-card/exam-card.component';

const ISO_DATE_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-exam-list',
  standalone: true,
  imports: [
    AutocompleteDropdownComponent,
    CommonModule,
    ContentBoxComponent,
    ExamCardComponent,
    FormsModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatTableModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }, // This sets the locale to use ISO date format
    { provide: MAT_DATE_FORMATS, useValue: ISO_DATE_FORMATS },
    { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl },
  ],
  templateUrl: './exam-list.component.html',
  styleUrl: './exam-list.component.scss',
})
export class ExamListComponent implements OnInit, OnDestroy {
  subscriptions = new Subscription();
  isClearingDateRange = false;

  requestList: ExamRequest[] = [];
  nonReads: number[] = [];
  labels_paginators = Labels.PAGINATORS;
  labels = Labels.EXAM_LIST;
  count = 0;
  pageSize = 10;
  pageIndex = 0;

  hidePageSize = true;
  showFirstLastButtons = true;

  isDesktop = false;
  isMobile = false;
  isPortrait = false;

  loading = true;

  possibleStatus: any[] = Object.entries(this.labels.STATUS_MAP).map(
    ([key, value]) => ({
      title: value,
      value: key,
    }),
  );
  possibleUrgency: any[] = Object.entries(this.labels.URGENCY_MAP).map(
    ([key, value]) => ({
      title: value,
      value: key,
    }),
  );
  selectedStatus: any[] = [];
  selectedUrgency: any[] = [];

  displayedColumns: string[] = [
    'patient_name',
    'code',
    'status',
    'urgent',
    'nonReads',
    'assigned_to',
    'created_at',
    'finished_at',
  ];

  dataSource: MatTableDataSource<ExamRequest>;
  patientControl = new FormControl();
  doctorControl = new FormControl();
  allPatients: Patient[] = [];
  allDoctors: User[] = [];
  loggedUser: User | null | undefined = null;

  displayPatientFn = (patient: Patient): string => {
    return patient && patient.name ? patient.displayName() : '';
  };

  displayDoctorFn = (doctor: User): string => {
    return doctor && doctor.full_name ? doctor.full_name : '';
  };

  displayStatusFn = (status: any): string => {
    return status.title;
  };

  displayUrgencyFn = (urgency: any): string => {
    return urgency.title;
  };

  dateRange: FormGroup;

  constructor(
    private examService: ExamService,
    private router: Router,
    private discussionService: DiscussionService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<Date>,
    private authService: AuthService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.dataSource = new MatTableDataSource<ExamRequest>([]);
    this.dateRange = this.fb.group({
      start: [null],
      end: [null],
    });
    this.dateAdapter.setLocale(this.locale);
    this.loggedUser = this.authService.getLoggedUser();
  }

  handlePageEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    const currentFilterData = this.examService.filterData;
    currentFilterData.page_size = this.pageSize;
    currentFilterData.page = this.pageIndex;
    this.examService.updateFilters(currentFilterData);
    this.updateList();
  }

  ngOnInit() {
    this.setDisplayedColumns();
    this.updateList();

    this.dataSource.sortingDataAccessor = (
      item: ExamRequest,
      property: string,
    ): string | number => {
      switch (property) {
        case 'patient_name':
          return item.patient?.name ?? '';
        case 'nonReads':
          return this.nonReads[this.requestList.indexOf(item)] ?? 0;
        case 'assigned_to':
          return item.assigned_to?.full_name || '';
        case 'created_at':
          return item.created_at;
        default:
          // eslint-disable-next-line no-case-declarations
          const value = item[property as keyof ExamRequest];
          if (typeof value === 'string' || typeof value === 'number') {
            return value;
          }
          if (typeof value === 'string' || typeof value === 'number') {
            return value;
          }
          return '';
      }
    };

    const resizeObservable = fromEvent(window, 'resize');
    const resizeSubscription = resizeObservable
      .pipe(
        debounceTime(300), // Debounce to avoid excessive calls
      )
      .subscribe(() => {
        this.setDisplayedColumns();
        this.updateList();
      });
    this.subscriptions.add(resizeSubscription);

    this.loadPatientsAndDoctors();

    this.dateRange.valueChanges.subscribe(() => {
      if (!this.isClearingDateRange) {
        const startDate = this.dateRange.get('start')?.value;
        const endDate = this.dateRange.get('end')?.value;
        if (startDate && endDate) {
          this.updateList();
        }
      }
    });

    this.dateAdapter.setLocale('en-GB');
    this.dateAdapter.format = this.formatDateISO;
  }

  loadPatientsAndDoctors() {
    const patientsSub = this.examService.getPatients().subscribe(
      (patients) => {
        this.allPatients = patients;
      },
      (error) => {
        console.error('Error loading patients:', error);
        this.allPatients = [];
      },
    );

    const doctorsSub = this.discussionService.getDoctors().subscribe(
      (doctors) => {
        this.allDoctors = doctors;
      },
      (error) => {
        console.error('Error loading doctors:', error);
        this.allDoctors = [];
      },
    );

    this.subscriptions.add(patientsSub);
    this.subscriptions.add(doctorsSub);
  }

  clearSelection(controlName: 'patient' | 'doctor') {
    const control =
      controlName === 'patient' ? this.patientControl : this.doctorControl;
    control.setValue(null);
    this.updateList();
  }

  allowEditOrDelete(exam: ExamRequest): boolean {
    return (
      exam.creator?.id === this.loggedUser?.id ||
      this.loggedUser?.role === UserRole.ADMIN
    );
  }

  setDisplayedColumns() {
    const isAdmin = this.loggedUser?.role === UserRole.ADMIN;
    this.isDesktop = window.innerWidth > 1400;
    this.isMobile = window.innerWidth < 1000;
    this.isPortrait = window.innerWidth < 768;
    if (this.isDesktop) {
      this.displayedColumns = [
        'patient_name',
        'code',
        'status',
        'urgent',
        'nonReads',
        'assigned_to',
        'created_at',
        'finished_at',
      ];
    } else if (this.isMobile && !this.isPortrait) {
      this.displayedColumns = [
        'patient_name',
        'status',
        'urgent',
        'created_at',
      ];
    } else if (this.isPortrait) {
      this.displayedColumns = ['patient_name', 'status'];
    } else {
      this.displayedColumns = [
        'patient_name',
        'code',
        'status',
        'urgent',
        'assigned_to',
        'created_at',
      ];
    }
    if (!this.isPortrait) {
      this.displayedColumns.push('edit');
    }
  }

  updateList() {
    this.loading = true;
    const selectedStatus = this.selectedStatus.map((s) => s.value);
    const selectedUrgency = this.selectedUrgency.map((u) => u.value);
    const currentFilterData = this.examService.filterData;
    currentFilterData.status = selectedStatus;
    currentFilterData.urgent = selectedUrgency;
    currentFilterData.patient_id = this.patientControl.value?.id || null;
    currentFilterData.assigned_to_id = this.doctorControl.value?.id || null;

    // Calculate the pageSize based on the display height
    const displayHeight = window.innerHeight;
    currentFilterData.page_size = Math.floor(
      (16 * (displayHeight - 350)) / 1174,
    );
    // Ensure the pageSize is at least 1
    currentFilterData.page_size = Math.max(1, currentFilterData.page_size);

    if (this.dateRange.valid) {
      const startDate = this.dateRange.get('start')?.value;
      const endDate = this.dateRange.get('end')?.value;
      if (startDate && endDate) {
        currentFilterData.created_at = `${this.formatDateISO(startDate)},${this.formatDateISO(endDate)}`;
      } else {
        currentFilterData.created_at = null;
      }
    } else {
      currentFilterData.created_at = null;
    }
    this.examService.updateFilters(currentFilterData);

    const sub = this.examService.getExams().subscribe((data: any) => {
      this.requestList = data.results as ExamRequest[];
      this.dataSource.data = this.requestList;
      this.nonReads = data.results.map(() => 0);
      this.count = data.count;
      this.pageSize = this.examService.filterData.page_size;
      this.pageIndex = this.examService.filterData.page;
      if (this.count < this.pageSize * this.pageIndex) {
        this.pageIndex = Math.floor(this.count / this.pageSize);
        const currentFilterData = this.examService.filterData;
        currentFilterData.page = this.pageIndex;
        this.examService.updateFilters(currentFilterData);
      }
      this.loading = false;
    });
    this.subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handleExamSelected(exam: ExamRequest): void {
    this.examService.setCurrentExam(exam);
    const url = this.isPortrait
      ? `standalone_viewer/${exam.id}`
      : `dashboard/${exam.id}`;
    this.router.navigate([url]);
  }

  handleExamEdit(exam: ExamRequest): void {
    if (!this.allowEditOrDelete(exam)) {
      return;
    }
    const url = `create_exam/${exam.id}`;
    this.router.navigate([url]);
  }

  confirmDelete(exam: ExamRequest): void {
    if (!this.allowEditOrDelete(exam)) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.labels.DELETE_EXAM_CONFIRMATION,
        message: this.labels.DELETE_EXAM_CONFIRMATION_TEXT,
        confirmText: this.labels.DELETE_EXAM_CONFIRM,
        cancelText: this.labels.DELETE_EXAM_CANCEL,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.handleExamDelete(exam);
      }
    });
  }

  handleExamDelete(exam: ExamRequest): void {
    this.examService.deleteExam(exam.id).subscribe({
      next: () => {
        this.toastr.success(this.labels.DELETE_EXAM_SUCCESS);
        this.updateList();
      },
      error: (error) => {
        this.toastr.error(this.labels.DELETE_EXAM_ERROR);
      },
    });
  }

  getUrgencyLabel(urgency: string): string {
    return (
      this.labels.URGENCY_MAP[
        urgency as keyof typeof this.labels.URGENCY_MAP
      ] ?? ''
    );
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'NOT_STARTED':
        return this.labels.STATUS_MAP.NOT_STARTED;
      case 'STARTED':
        return this.labels.STATUS_MAP.STARTED;
      case 'FINISHED':
        return this.labels.STATUS_MAP.FINISHED;
      default:
        return '';
    }
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'patient_name':
          return this.compare(a.patient?.name, b.patient?.name, isAsc);
        case 'code':
          return this.compare(a.code, b.code, isAsc);
        case 'status':
          return this.compare(
            this.getStatusLabel(a.status),
            this.getStatusLabel(b.status),
            isAsc,
          );
        case 'urgent':
          return this.compare(
            this.getUrgencyLabel(a.urgent + ''),
            this.getUrgencyLabel(b.urgent + ''),
            isAsc,
          );
        case 'nonReads':
          return this.compare(
            this.nonReads[this.requestList.indexOf(a)],
            this.nonReads[this.requestList.indexOf(b)],
            isAsc,
          );
        case 'assigned_to':
          return this.compare(
            a.assigned_to?.full_name || '',
            b.assigned_to?.full_name || '',
            isAsc,
          );
        case 'created_at':
          return this.compare(a.created_at, b.created_at, isAsc);
        default:
          return 0;
      }
    });
  }

  private compare(
    a: number | string | undefined,
    b: number | string | undefined,
    isAsc: boolean,
  ): number {
    if (a === undefined && b === undefined) return 0;
    if (a === undefined) return isAsc ? -1 : 1;
    if (b === undefined) return isAsc ? 1 : -1;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private formatDateISO(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  onPatientSelectionChange(patient: Patient | null) {
    this.patientControl.setValue(patient);
    this.updateList();
  }

  onDoctorSelectionChange(doctor: User | null) {
    this.doctorControl.setValue(doctor);
    this.updateList();
  }

  clearDateRange() {
    this.isClearingDateRange = true;
    this.dateRange.reset();
    this.updateList();
    setTimeout(() => {
      this.isClearingDateRange = false;
    }, 0);
  }

  onStatusSelectionChange(selectedStatus: any[]) {
    this.selectedStatus = selectedStatus;
    this.updateList();
  }

  onUrgencySelectionChange(selectedUrgency: any[]) {
    this.selectedUrgency = selectedUrgency;
    this.updateList();
  }

  isFromLis(exam: any) {
    return (
      exam.code !== '' &&
      exam.num_images_from_lis !== null &&
      exam.num_images_from_lis !== undefined &&
      exam.num_images_from_lis > 0
    );
  }

  getImageStatus(exam: any) {
    if (this.isFromLis(exam)) {
      return `${this.labels.IMAGES} ${exam.num_digitalized}/${exam.num_images_from_lis}`;
    } else {
      return '';
    }
  }
}
